import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApiClient } from "../../api/ApiClient";
import { handleUnauthorized } from "../../utils/auth/authHandler";

// Thunk action to fetch user profile from API
// This is the single source of truth for user data
export const fetchUserProfile = createAsyncThunk(
  "auth/fetchUserProfile",
  async (_, { rejectWithValue, getState }) => {
    try {
      // Check if we just logged out - don't fetch profile in that case
      const currentState = getState();
      if (
        !currentState.auth.isAuthenticated &&
        currentState.auth.user === null &&
        !currentState.auth.isLoading
      ) {
        return rejectWithValue("logged_out");
      }

      // Try to get CSRF token, allowing 401 to return null instead of throwing
      const csrfToken = await ApiClient.getCsrfToken(true);

      // If CSRF token is null, Knox cookie has expired
      if (csrfToken === null) {
        // Call centralized handler to clean up and redirect
        handleUnauthorized();
        return rejectWithValue("unauthorized");
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_DOMAIN}/account/get-account`,
        {
          method: "GET",
          credentials: "include",
        },
      );

      if (response.ok) {
        const data = await response.json();
        const account = data.account;

        // Map API response to user object
        return {
          user_name: account.user_name,
          email: account.email,
          user_role: account.user_role,
          has_usable_password: account.has_usable_password,
          avatar: account.personal_info?.avatar || null,
          account_status: account.status || null,
          stripe_connected_account: account.stripe_connected_account || false,
          preferences: account.preferences || null,
          affiliate_status: account.affiliate_status || null,
          affiliates_enabled: account.affiliates_enabled || false,
          financial_insights: account.financial_insights || {
            is_enabled: false,
            personal_finances: false,
            investments: false,
            credit_score: false,
          },
          ista: account.ista || false,
        };
      } else if (response.status === 401) {
        // User is not authenticated - Knox cookie expired
        handleUnauthorized();
        return rejectWithValue("unauthorized");
      } else {
        return rejectWithValue("error");
      }
    } catch (error) {
      return rejectWithValue("network_error");
    }
  },
);

const initialState = {
  // Always start as not authenticated
  // Knox HTTP-only cookie is the ONLY source of truth
  // RootNavBar will dispatch fetchUserProfile on mount to verify with backend
  isAuthenticated: false,
  isLoading: true, // Start with loading = true to prevent flicker on page load
  user: null,
  showSessionExpiredModal: false,
  isSidebarOpen: false,
  currentSegments: [],
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Called after successful login to set user data
    loginRdx: (state, action) => {
      state.isAuthenticated = true;
      state.isLoading = false;
      state.showSessionExpiredModal = false;
      if (action.payload) {
        state.user = action.payload;
      }
    },
    // Called on logout
    logoutRdx: (state) => {
      state.isAuthenticated = false;
      state.isLoading = false;
      state.user = null;
      state.showSessionExpiredModal = false;
      ApiClient.initializeApiClient();
    },
    // Called when the session expire, auth token expired
    setSessionExpired: (state, action) => {
      state.showSessionExpiredModal = action.payload;
    },
    // Update user status (cpa_status, connected_account, etc.)
    // Used by onboarding components when status changes
    updateUserStatus: (state, action) => {
      if (state.user) {
        const {
          account_status,
          stripe_connected_account,
          preferences,
          affiliate_status,
          affiliates_enabled,
          financial_insights,
          ista,
        } = action.payload;

        if (account_status !== undefined) {
          state.user.account_status = account_status;
        }

        if (stripe_connected_account !== undefined) {
          state.user.stripe_connected_account = stripe_connected_account;
        }

        if (preferences !== undefined) {
          state.user.preferences = preferences;
        }

        if (affiliate_status !== undefined) {
          state.user.affiliate_status = affiliate_status;
        }

        if (affiliates_enabled !== undefined) {
          state.user.affiliates_enabled = affiliates_enabled;
        }

        if (financial_insights !== undefined) {
          state.user.financial_insights = financial_insights;
        }

        if (ista !== undefined) {
          state.user.ista = ista;
        }
      }
    },
    toggleSidebar: (state) => {
      state.isSidebarOpen = !state.isSidebarOpen;
    },
    closeSidebar: (state) => {
      state.isSidebarOpen = false;
    },
    setSegments: (state, action) => {
      state.currentSegments = action.payload;
    },
    clearSegments: (state) => {
      state.currentSegments = [];
    },
  },
  extraReducers: (builder) => {
    // Handle fetchUserProfile
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.isLoading = false;
        state.user = action.payload;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        if (action.payload === "unauthorized") {
          // If we want the modal to show, we actually DON'T want to
          // wipe the auth state immediately, or the UI will jump to public.
          // Only wipe it if the modal is NOT going to show.
          if (!state.showSessionExpiredModal) {
            state.isAuthenticated = false;
            state.user = null;
          }
        }
      });
  },
});

export const {
  loginRdx,
  logoutRdx,
  updateUserStatus,
  setSessionExpired,
  toggleSidebar,
  closeSidebar,
  setSegments,
  clearSegments,
} = authSlice.actions;
export default authSlice.reducer;
